import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useField, useForm } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import WithError from '../../../components/with-error/WithError';
import DropdownWithIcons from '../../../components/common-dropdown/DropdownWithIcons';
import DocsButton from '../../../scout/components/generation-section/components/docs-button/DocsButton';
import usePlaygroundOptions from './usePlaygroundOptions';
import { ASSISTANT_TYPES } from '../../../constants';
import { resetSession, uploadFile } from '../../../redux/ai-playground/aiPlaygroundActions';
import { setSelectedAssistant } from '../../../redux/ai-playground/aiPlaygroundReducer';
import ChangeAssistantModal from '../change-assistant-modal/ChangeAssistantModal';

import scss from './AssistantFields.module.scss';

function AssistantFields() {
  const { t } = useTranslation('scout');
  const dispatch = useDispatch();

  const form = useForm();
  const assistantInput = useField('assistant').input;

  const [possibleNewAssistant, setPossibleNewAssistants] = useState(null);

  const typeOptions = usePlaygroundOptions();
  const isFormLoading = useSelector((state) => state.aiPlayground.isFormLoading);
  const isGenerating = useSelector((state) => state.aiPlayground.isGenerating);
  const isResetting = useSelector((state) => state.aiPlayground.isResetting);
  const isFileUploading = useSelector((state) => state.aiPlayground.isFileUploading);
  const threadId = useSelector((state) => state.aiPlayground.threadId);

  const disableFields = isFormLoading || isResetting || isGenerating;
  const disableButton =
    isFormLoading ||
    isResetting ||
    isFileUploading ||
    isGenerating ||
    assistantInput.value?.type !== ASSISTANT_TYPES.FILE;

  const handleAssistantClick = (newAssistant) => {
    const assistantData = typeOptions.find(({ value }) => value === newAssistant);

    const formattedAssistantData = {
      value: assistantData.value,
      label: assistantData.label,
      name: assistantData.name,
      type: assistantData.type,
    };

    if (!assistantInput.value?.value || !threadId) {
      dispatch(setSelectedAssistant(formattedAssistantData));
      form.change('assistant', formattedAssistantData);
      return;
    }

    if (formattedAssistantData.value !== assistantInput.value?.value) {
      setPossibleNewAssistants(formattedAssistantData);
    }
  };

  const handleCancelAssistantChanges = () => {
    setPossibleNewAssistants(null);
  };

  const handleChangeAssistantAndReset = async () => {
    const newAssistant = possibleNewAssistant;
    setPossibleNewAssistants(null);

    await dispatch(resetSession());
    dispatch(setSelectedAssistant(newAssistant));
    form.change('assistant', newAssistant);
    form.change('fileText', '');
  };

  useEffect(() => {
    if (typeOptions.length && !assistantInput.value) {
      const assistant = typeOptions[0];
      form.change('assistant', assistant);
      dispatch(
        setSelectedAssistant({
          value: assistant.value,
          label: assistant.label,
          name: assistant.name,
          type: assistant.type,
        }),
      );
    }
  }, [typeOptions, assistantInput, form, dispatch]);

  return (
    <div className={scss.assistantsFieldsWrapper}>
      <Field name='assistant'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.ROW} label={t('type')} isBold>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <DropdownWithIcons
                options={typeOptions}
                value={input.value?.value}
                onSelect={handleAssistantClick}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                name={input.name}
                placeholder={t('select')}
                isError={meta.touched && meta.error}
                disabled={disableFields}
                stressedIcons
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <DocsButton
        selectedTypeOption={assistantInput.value}
        onFileUpload={uploadFile}
        disabled={disableButton}
        isLoading={isFileUploading}
      />

      <ChangeAssistantModal
        isOpen={!!possibleNewAssistant}
        onCancel={handleCancelAssistantChanges}
        onChangeAndReset={handleChangeAssistantAndReset}
      />
    </div>
  );
}

export default AssistantFields;
